import {
  differenceInCalendarDays,
  format,
  formatDistanceToNowStrict,
  isValid,
  parseISO,
} from 'date-fns';
import { it } from 'date-fns/locale';

const capitalize = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

function toDate(value: string | Date): Date {
  return typeof value === 'string' ? parseISO(value) : value;
}

/** "Martedì 20 ottobre" */
export function formatDayLong(value: string | Date): string {
  const d = toDate(value);
  return isValid(d) ? capitalize(format(d, 'EEEE d MMMM', { locale: it })) : '';
}

/** "20 ott" */
export function formatDayShort(value: string | Date): string {
  const d = toDate(value);
  return isValid(d) ? format(d, 'd MMM', { locale: it }) : '';
}

/** "14:30" */
export function formatTime(value: string | Date): string {
  const d = toDate(value);
  return isValid(d) ? format(d, 'HH:mm') : '';
}

/** "oggi 14:30", "domani 9:00", "giovedì 10:00", "12 nov 10:00" */
export function formatWhen(value: string | Date, now = new Date()): string {
  const d = toDate(value);
  if (!isValid(d)) return '';
  const days = differenceInCalendarDays(d, now);
  const time = format(d, 'H:mm');
  if (days === 0) return `oggi ${time}`;
  if (days === 1) return `domani ${time}`;
  if (days === -1) return `ieri ${time}`;
  if (days > 1 && days < 7) return `${format(d, 'EEEE', { locale: it })} ${time}`;
  return `${format(d, 'd MMM', { locale: it })} ${time}`;
}

/** "3 minuti fa" */
export function formatAgo(value: string | Date): string {
  const d = toDate(value);
  return isValid(d) ? `${formatDistanceToNowStrict(d, { locale: it })} fa` : '';
}

const intFormat = new Intl.NumberFormat('it-IT');
const compactFormat = new Intl.NumberFormat('it-IT', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export const formatNumber = (n: number) => intFormat.format(n);
export const formatCompact = (n: number) =>
  Math.abs(n) >= 10_000 ? compactFormat.format(n) : intFormat.format(n);

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toLocaleString('it-IT', { maximumFractionDigits: value < 10 ? 1 : 0 })} ${units[unit]}`;
}

export function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${m}:${String(rest).padStart(2, '0')}`;
}

/** Plurale italiano semplice: plural(3, 'attività', 'attività'), plural(1, 'post', 'post'). */
export function plural(n: number, one: string, many: string): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}
